import React, {useEffect, useRef, useState} from 'react';
import {Button, Dimmer, Form, Input, Loader, Modal, Segment} from 'semantic-ui-react';
import {toast} from 'react-toastify';
import {apiPost} from '../../../services/apiClient';
import {
    applyMassCalculationAddressCorrection,
    updateMassCalculationRouteAddress
} from '../../../services/massUploadApi';

const mapSuggestionToAddressPayload = (address) => {
    if (!address) {
        return null;
    }

    return {
        fullName: address.fullName || address.addressName || address.name || '',
        postalCode: address.postalCode,
        fias: address.fias,
        country: address.country,
        countryCode: address.countryCode,
        region: address.region,
        city: address.city,
        settlement: address.settlement,
        street: address.street,
        house: address.house,
        building: address.building,
        office: address.office,
        geoLat: address.geoLat,
        geoLon: address.geoLon
    };
};

/**
 * Модалка редактирования адреса маршрута массового расчета
 */
const EditRouteAddressModal = ({open, route, allRoutes, fileId, fieldName, title, onClose, onSave}) => {
    const [loading, setLoading] = useState(false);
    const [value, setValue] = useState('');
    const [suggestions, setSuggestions] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [selectedAddress, setSelectedAddress] = useState(null);
    const [isAddressManuallyEdited, setIsAddressManuallyEdited] = useState(false);
    const [bulkCorrectionModal, setBulkCorrectionModal] = useState({
        open: false,
        count: 0,
        originalAddress: '',
        correctedAddress: '',
        addressFromSuggestions: null,
        savedValue: null
    });
    const suggestionTimeoutRef = useRef(null);
    const mountedRef = useRef(true);

    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
            if (suggestionTimeoutRef.current) {
                clearTimeout(suggestionTimeoutRef.current);
            }
        };
    }, []);

    useEffect(() => {
        if (!open || !route || !fieldName || !mountedRef.current) {
            return;
        }

        const initialValue = fieldName === 'fromAddress'
            ? (route.fromAddress || '')
            : (route.toAddress || '');
        const hasAddressError = fieldName === 'fromAddress' ? route.fromHasError : route.toHasError;

        setValue(initialValue);
        setSuggestions([]);
        setShowSuggestions(false);
        setBulkCorrectionModal({
            open: false,
            count: 0,
            originalAddress: '',
            correctedAddress: '',
            addressFromSuggestions: null,
            savedValue: null
        });

        if (initialValue && !hasAddressError) {
            setSelectedAddress({
                fullName: initialValue,
                addressName: initialValue,
                name: initialValue
            });
            setIsAddressManuallyEdited(false);
        } else {
            setSelectedAddress(null);
            setIsAddressManuallyEdited(false);
        }
    }, [open, route, fieldName]);

    const fetchAddressSuggestions = (text) => {
        if (!text || text.length < 3) {
            if (mountedRef.current) {
                setSuggestions([]);
                setShowSuggestions(false);
            }
            return;
        }

        if (suggestionTimeoutRef.current) {
            clearTimeout(suggestionTimeoutRef.current);
        }

        suggestionTimeoutRef.current = setTimeout(() => {
            apiPost('parcels/addresses/suggestions', { text, maxCount: 10, countryCode: 'RU' })
                .then(data => {
                    if (!mountedRef.current) return;
                    if (data && data.entries) {
                        setSuggestions(data.entries);
                        setShowSuggestions(true);
                    }
                })
                .catch(() => {
                    if (!mountedRef.current) return;
                    setSuggestions([]);
                    setShowSuggestions(false);
                });
        }, 300);
    };

    const handleValueChange = (newValue) => {
        if (!mountedRef.current) return;

        setValue(newValue);
        if (selectedAddress && newValue !== (selectedAddress.fullName || selectedAddress.addressName || selectedAddress.name || '')) {
            setSelectedAddress(null);
            setIsAddressManuallyEdited(true);
        }
        fetchAddressSuggestions(newValue);
    };

    const handleAddressSelect = (address) => {
        if (!mountedRef.current) return;
        const fullAddress = address.fullName || address.addressName || address.name || '';
        setValue(fullAddress);
        setSelectedAddress(address);
        setIsAddressManuallyEdited(false);
        setShowSuggestions(false);
        setSuggestions([]);
    };

    const findMatchingRoutesWithSameError = (originalAddress) => {
        return (allRoutes || []).filter(other => {
            if (!other || other.id === route.id) {
                return false;
            }

            const otherValue = fieldName === 'fromAddress' ? other.fromAddress : other.toAddress;
            const hasAddressError = fieldName === 'fromAddress' ? other.fromHasError : other.toHasError;
            return String(otherValue || '') === String(originalAddress) && hasAddressError;
        });
    };

    const handleSave = () => {
        if (!route || !fieldName || !mountedRef.current) return;

        if (!selectedAddress && value.trim()) {
            toast.error('Пожалуйста, выберите адрес из списка предложений. Нельзя сохранить адрес, введенный вручную.');
            return;
        }

        if (!selectedAddress) {
            toast.error('Выберите адрес из списка предложений');
            return;
        }

        setLoading(true);
        const originalAddress = fieldName === 'fromAddress'
            ? (route.fromAddress || '')
            : (route.toAddress || '');
        const addressText = selectedAddress.fullName || selectedAddress.addressName || selectedAddress.name || value;
        const payload = {
            fieldName,
            addressText,
            addressFromSuggestions: mapSuggestionToAddressPayload(selectedAddress)
        };

        updateMassCalculationRouteAddress(route.id, payload)
            .then(() => {
                if (!mountedRef.current) return;
                toast.success('Адрес обновлен');
                setLoading(false);

                if (String(originalAddress) !== String(addressText)) {
                    const matchingRoutes = findMatchingRoutesWithSameError(originalAddress);
                    if (matchingRoutes.length > 0) {
                        setBulkCorrectionModal({
                            open: true,
                            count: matchingRoutes.length,
                            originalAddress,
                            correctedAddress: addressText,
                            addressFromSuggestions: selectedAddress,
                            savedValue: addressText
                        });
                        return;
                    }
                }

                onSave && onSave(route.id, fieldName, addressText);
                onClose && onClose();
            })
            .catch(err => {
                if (!mountedRef.current) return;
                console.error(err);
                const message = (err && (err.Error || err.error || err.message))
                    || 'Ошибка при обновлении адреса';
                toast.error(message);
                setLoading(false);
                // Даже при ошибке валидации данные могли сохраниться с флагом ошибки — обновляем таблицу
                onSave && onSave(route.id, fieldName, addressText);
            });
    };

    const handleApplyToAll = () => {
        if (!bulkCorrectionModal.open || !mountedRef.current || !fileId) return;

        setLoading(true);
        applyMassCalculationAddressCorrection(fileId, {
            fieldName,
            originalAddress: bulkCorrectionModal.originalAddress,
            correctedAddress: bulkCorrectionModal.correctedAddress,
            addressFromSuggestions: mapSuggestionToAddressPayload(bulkCorrectionModal.addressFromSuggestions)
        })
            .then((result) => {
                if (!mountedRef.current) return;
                const updatedCount = (result && result.updatedCount) || bulkCorrectionModal.count;
                toast.success(`Исправление применено к ${updatedCount} маршрутам`);
                setBulkCorrectionModal({
                    open: false,
                    count: 0,
                    originalAddress: '',
                    correctedAddress: '',
                    addressFromSuggestions: null,
                    savedValue: null
                });
                setLoading(false);
                onSave && onSave(route.id, fieldName, bulkCorrectionModal.savedValue);
                onClose && onClose();
            })
            .catch(err => {
                if (!mountedRef.current) return;
                console.error(err);
                toast.error('Ошибка при массовом применении исправления');
                setLoading(false);
            });
    };

    const handleApplyOnlyHere = () => {
        if (!mountedRef.current) return;
        setBulkCorrectionModal({
            open: false,
            count: 0,
            originalAddress: '',
            correctedAddress: '',
            addressFromSuggestions: null,
            savedValue: null
        });
        onSave && onSave(route.id, fieldName, bulkCorrectionModal.savedValue);
        onClose && onClose();
    };

    const handleClose = () => {
        if (loading) return;
        onClose && onClose();
    };

    if (!route || !fieldName) {
        return null;
    }

    return (
        <React.Fragment>
            <Modal
                closeIcon
                onClose={handleClose}
                open={open}
                size="small"
            >
                <Modal.Header>
                    Изменить поле "{title}" для № ПП {route.rowNumber}
                </Modal.Header>

                <Segment vertical style={{borderBottom: 'none'}}>
                    <Dimmer active={loading} inverted>
                        <Loader size='medium'/>
                    </Dimmer>
                    <Modal.Content className='modal__content'>
                        <Form>
                            <Form.Field>
                                <label>{title}</label>
                                <div style={{marginBottom: '8px', fontSize: '12px', color: '#666'}}>
                                    <i className="fa fa-info-circle"></i> Для сохранения адреса необходимо выбрать его из списка предложений
                                </div>
                                <div style={{position: 'relative'}}>
                                    <Input
                                        fluid
                                        value={value}
                                        onChange={(e, {value: nextValue}) => handleValueChange(nextValue)}
                                        placeholder={`Введите ${title}`}
                                        autoFocus
                                        error={isAddressManuallyEdited && !selectedAddress && value.trim() ? {
                                            content: 'Выберите адрес из списка предложений',
                                            pointing: 'below'
                                        } : false}
                                    />

                                    {showSuggestions && suggestions.length > 0 && (
                                        <div style={{
                                            position: 'absolute',
                                            top: '100%',
                                            left: 0,
                                            right: 0,
                                            backgroundColor: '#fff',
                                            border: '1px solid #ccc',
                                            borderRadius: '4px',
                                            maxHeight: '300px',
                                            overflowY: 'auto',
                                            zIndex: 1000,
                                            boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                                        }}>
                                            {suggestions.map((addr, idx) => (
                                                <div
                                                    key={idx}
                                                    style={{
                                                        padding: '8px 12px',
                                                        cursor: 'pointer',
                                                        borderBottom: idx < suggestions.length - 1 ? '1px solid #eee' : 'none'
                                                    }}
                                                    onMouseDown={(e) => {
                                                        e.preventDefault();
                                                        handleAddressSelect(addr);
                                                    }}
                                                    onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f0f0f0'; }}
                                                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#fff'; }}
                                                >
                                                    <div style={{fontWeight: '500'}}>{addr.fullName}</div>
                                                    {addr.description && (
                                                        <div style={{fontSize: '12px', color: '#666', marginTop: '2px'}}>
                                                            {addr.description}
                                                        </div>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </Form.Field>
                        </Form>
                    </Modal.Content>
                </Segment>

                <Modal.Actions>
                    <Button onClick={handleClose} disabled={loading}>
                        Отмена
                    </Button>
                    <Button
                        primary
                        disabled={loading || (!selectedAddress && !!value.trim())}
                        onClick={handleSave}
                        loading={loading}
                        title={!selectedAddress && !!value.trim() ? 'Выберите адрес из списка предложений' : ''}
                    >
                        Сохранить
                    </Button>
                </Modal.Actions>
            </Modal>

            <Modal
                closeIcon
                onClose={handleApplyOnlyHere}
                open={bulkCorrectionModal.open}
                size="small"
            >
                <Modal.Header>Применить исправление ко всем таким адресам?</Modal.Header>
                <Modal.Content className='modal__content'>
                    <div>
                        <p>
                            Адрес <strong>{bulkCorrectionModal.originalAddress}</strong> с ошибкой встречается ещё в{' '}
                            <strong>{bulkCorrectionModal.count}</strong> маршрутах.
                        </p>
                        <p>
                            Заменить на <strong>{bulkCorrectionModal.correctedAddress}</strong>?
                        </p>
                    </div>
                </Modal.Content>
                <Modal.Actions className='modal__actions'>
                    <Button onClick={handleApplyOnlyHere} disabled={loading}>
                        Только здесь
                    </Button>
                    <Button
                        onClick={handleApplyToAll}
                        className='modal__actions_yes'
                        disabled={loading}
                        loading={loading}
                    >
                        Применить ко всем ({bulkCorrectionModal.count})
                    </Button>
                </Modal.Actions>
            </Modal>
        </React.Fragment>
    );
};

export default EditRouteAddressModal;



// WEBPACK FOOTER //
// ./App/pages/massUpload/components/EditRouteAddressModal.jsx